import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { COLLECTIONS, PRICE } from "../data/collections";
import { colorOf } from "../data/site";
import Reveal from "./Reveal";
import QuickViewModal from "./QuickViewModal";
import { SecondaryDoodle } from "./PersonalityDoodles";

export default function CollectionGrid({ items, title = "Shop by collection", subtitle, all = false, limit = null }) {
  const base = items || COLLECTIONS;
  const list = limit ? base.slice(0, limit) : base;
  const hasMore = !items && limit && base.length > limit;
  const [quickSlug, setQuickSlug] = useState(null);

  return (
    <section className="relative py-24 md:py-32 px-5 md:px-10 bg-[#FAFAF7]" data-testid="collection-grid">
      <div className="mx-auto max-w-[1440px]">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14">
          <Reveal>
            <div>
              <p className="eyebrow mb-4">{title}</p>
              <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.025em]">
                Every frame, <span className="italic">a personality.</span>
              </h2>
            </div>
          </Reveal>
          {subtitle && (
            <Reveal delay={80}>
              <p className="text-[#595955] max-w-[36ch] text-base md:text-lg">{subtitle}</p>
            </Reveal>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
          {list.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 6) * 50}>
              <CollectionCard c={c} onQuickView={() => setQuickSlug(c.slug)} />
            </Reveal>
          ))}
        </div>

        {hasMore && (
          <Reveal delay={120}>
            <div className="mt-12 md:mt-16 flex justify-center">
              <Link
                to="/collections"
                data-testid="collection-grid-view-all"
                className="inline-flex items-center gap-2 rounded-full border border-[#2C2C2A] text-[#2C2C2A] hover:bg-[#2C2C2A] hover:text-[#FAFAF7] transition-colors px-7 py-3.5 text-[14px] font-medium tracking-[0.02em]"
              >
                View all {base.length} frames →
              </Link>
            </div>
          </Reveal>
        )}
      </div>

      <QuickViewModal
        slug={quickSlug}
        open={!!quickSlug}
        onOpenChange={(o) => !o && setQuickSlug(null)}
      />
    </section>
  );
}

function CollectionCard({ c, onQuickView }) {
  return (
    <div className="group relative">
      <Link
        to={`/collection/${c.slug}`}
        className="block relative rounded-[24px] overflow-hidden bg-[#F3EADA] lift"
        data-testid={`collection-card-${c.slug}`}
        data-hover="true"
      >
        <div className="aspect-[4/5] overflow-hidden relative">
          <img
            src={`/products/${c.slug}-kid.jpg`}
            alt={`${c.name} sunglasses`}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms]"
            style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
          />
          {/* Personality doodle accent */}
          <div className="absolute top-3 left-3 opacity-90 pointer-events-none">
            <SecondaryDoodle personality={c.personality} className="!w-9 !h-9" />
          </div>
        </div>

        {/* Bottom info card */}
        <div className="absolute inset-x-3 bottom-3 rounded-2xl bg-[#FAFAF7]/95 backdrop-blur p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-serif text-2xl leading-none">{c.name}</h3>
            <p className="mt-1 text-[12px] text-[#595955] truncate">{c.audience}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {c.colors.slice(0, 4).map((col) => (
              <span key={col} className="swatch" style={{ background: colorOf(col) }} title={col} />
            ))}
            {c.colors.length > 4 && (
              <span className="text-[11px] text-[#595955] ml-1">+{c.colors.length - 4}</span>
            )}
          </div>
        </div>

        {/* Price chip */}
        <span className="absolute top-3 right-3 text-[11px] font-medium tracking-[0.15em] uppercase bg-[#FAFAF7] rounded-full px-2.5 py-1">
          {PRICE}
        </span>
      </Link>

      {/* Quick view trigger */}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onQuickView(); }}
        className="absolute top-12 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#2C2C2A] text-[#FAFAF7] px-3 py-1.5 text-[11px] font-medium opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-all duration-300 hover:bg-[#1A1A18]"
        data-testid={`quick-view-trigger-${c.slug}`}
        aria-label={`Quick view ${c.name}`}
      >
        <Eye className="w-3.5 h-3.5" /> Quick view
      </button>
    </div>
  );
}
